import React, { useState } from "react";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, TextField, Typography, Link, CircularProgress } from "@mui/material";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);

  // Email validation function (same as Login component)
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setEmailError("");
    setLoading(true);

    // Validate email format
    if (!email || email.trim() === "") {
      setEmailError("Please enter your email address");
      setIsEmailValid(false);
      setLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      setIsEmailValid(false);
      setLoading(false);
      return;
    }

    setIsEmailValid(true);

    try {
      const auth = getAuth();

      // Configure action code settings for custom password reset URL
      const actionCodeSettings = {
        url: `https://policytrackr-1.firebaseapp.com/reset-password`,
        handleCodeInApp: true,
      };

      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      setMessage("Password reset email sent! Please check your inbox and spam folder.");
    } catch (err) {
      console.error("Password reset error:", err);

      // Handle specific Firebase auth errors
      if (err.code === 'auth/user-not-found') {
        setError("No account found with this email address.");
      } else if (err.code === 'auth/invalid-email') {
        setError("Please enter a valid email address.");
        setEmailError("Please enter a valid email address");
        setIsEmailValid(false);
      } else if (err.code === 'auth/too-many-requests') {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(err.message || "Failed to send reset email. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    // Clear errors when user starts typing
    if (emailError) {
      setEmailError("");
      setIsEmailValid(true);
    }
    if (error) {
      setError("");
    }
    if (message) {
      setMessage("");
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          p: 4,
          borderRadius: 2,
          boxShadow: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" gutterBottom>
          Reset Password
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Enter your registered email to receive a reset link.
        </Typography>

        <Box component="form" onSubmit={handleReset} sx={{ mt: 2 }}>
          <TextField
            type="email"
            fullWidth
            label="Email Address"
            value={email}
            onChange={handleEmailChange}
            required
            error={!isEmailValid}
            helperText={emailError}
            margin="dense"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disableElevation
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Send Reset Link"}
          </Button>

          {message && (
            <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
              {message}
            </Typography>
          )}
          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Link 
            component={RouterLink} 
            to="/login" 
            variant="text"
            sx={{
              color: '#0051C3',
              fontWeight: 500,
              fontSize: '0.875rem',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
                fontWeight: 600,
              }
            }}
          >
            Back to Login
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;